import React, {
	forwardRef,
	ReactElement,
	Ref,
	useEffect,
	useState,
} from 'react';
import Dialog from '@mui/material/Dialog';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { GrClose } from 'react-icons/gr';
// icons
import mapPin from '../../../assets/images/icons/mapPin.svg';
import money from '../../../assets/images/icons/money.svg';
import time from '../../../assets/images/icons/time.svg';
// api
import { services } from '../../../apis';
import { JobDetailsData } from '../../../apis/JobPortalService';
import Loader from '../../../components/Loader';
import { PopupState } from '../jobportal';

const Transition = forwardRef(function Transition(
	props: TransitionProps & {
		children: ReactElement;
	},
	ref: Ref<unknown>
) {
	return (
		<Slide
			direction="up"
			ref={ref}
			{...props}
		/>
	);
});

interface PopupProps {
	jobDetailsPopup: PopupState;
	setJobDetailsPopup: (newState: PopupState) => void;
	setApplyPopup: (newState: PopupState) => void;
}

const JobDetailsPopup = ({
	jobDetailsPopup,
	setJobDetailsPopup,
	setApplyPopup,
}: PopupProps) => {
	const [loading, setLoading] = useState(false);
	const [jobDetailsData, setJobDetailsData] = useState({} as JobDetailsData);
	const popupIdentifer = jobDetailsPopup.identifier.split('source:')[0];
	const popupSource = jobDetailsPopup.identifier.split('source:')[1];

	useEffect(() => {
		const fetchJobDetails = async () => {
			if (popupIdentifer !== '') {
				setLoading(true);
				const data = await services.jobPortalService.fetchJobDetails(
					popupIdentifer
				);
				console.log(data);
				if (typeof data.data === 'string')
					setJobDetailsData({} as JobDetailsData);
				else setJobDetailsData(data.data);
				setTimeout(() => {
					setLoading(false);
				}, 300);
			}
		};

		fetchJobDetails();
	}, [jobDetailsPopup]);

	const handleClose = () => {
		setJobDetailsPopup({ isOpen: false, identifier: '' });
		setTimeout(() => {
			setJobDetailsData({} as JobDetailsData);
		}, 300);
	};

	const formatDate = (date: string) => {
		const newDate = new Date(date);
		const day = newDate.getDate();
		const month = newDate
			.toLocaleString('default', { month: 'long' })
			.substring(0, 3);
		const time = newDate.toLocaleTimeString([], {
			hour: '2-digit',
			minute: '2-digit',
		});

		return day + ' ' + month + ', ' + time;
	};

	const linkifyResources = (resources: string[]) => {
		let response = '';
		resources.forEach((resource) => {
			response += `<a href=${resource} target="_blank">${resource}</a><br />`;
		});
		return response;
	};

	// get data variables
	const {
		id,
		minCtcOffered,
		maxCtcOffered,
		deadline,
		jd,
		location,
		rolesOffered,
		selectionProcess,
		companyName,
		eligibility,
		disqualificationReasons,
		resources,
		isRejected,
		rejectedReason
	} = jobDetailsData;

	return (
		<Dialog
			open={jobDetailsPopup.isOpen}
			TransitionComponent={Transition}
			keepMounted
			onClose={handleClose}
			fullWidth={true}
			maxWidth="md"
			className="thin-scrollbar"
		>
			<div className="job-portal-job-details-popup-inner">
				{loading === true && (
					<div style={{ transform: 'scale(0.75)' }}>
						<Loader />
					</div>
				)}
				{loading === false &&
					Object.keys(jobDetailsData).length === 0 && (
						<p>Some error occurred, please try again later.</p>
					)}
				{loading === false && Object.keys(jobDetailsData).length !== 0 && (
					<>
						<div className="popup-header">
							<div>
								{companyName && <h2>{companyName}</h2>}
								<p>
									{location && (
										<span
											className={
												location.length > 1
													? 'custom-tooltip'
													: ''
											}
											data-tooltip={location.join(', ')}
										>
											<img src={mapPin} />{' '}
											{location.length > 1
												? 'Multiple Locations'
												: location[0]}
										</span>
									)}
									{minCtcOffered && maxCtcOffered && (
										<span>
											<img src={money} />{' '}
											{minCtcOffered +
												'LPA - ' +
												maxCtcOffered +
												'LPA'}
										</span>
									)}
								</p>
							</div>
							<GrClose
								onClick={() => {
									handleClose();
								}}
							/>
						</div>
						<div className="popup-body">
							<div className="meta">
								{rolesOffered && (
									<p>
										<span>OPEN POSITIONS: </span>{' '}
										{rolesOffered.join(', ')}
									</p>
								)}
								{selectionProcess && (
									<p>
										<span>TIMELINE:</span>{' '}
										{selectionProcess.join(' -> ')}
									</p>
								)}
							</div>
							{popupSource !== 'applications' &&
								jobDetailsPopup.identifier !== '' && (
									<div
										className={`info-box ${
											eligibility
												? 'isEligible'
												: 'isNotEligible'
										}`}
									>
										<h3>
											{eligibility
												? 'Congrats, You are eligible'
												: 'You are not eligible.'}
										</h3>
										{eligibility === false && (
											<p
												dangerouslySetInnerHTML={{
													__html: disqualificationReasons.join(
														'<br />'
													),
												}}
											></p>
										)}
									</div>
								)}
							{popupSource == 'applications' &&
								jobDetailsPopup.identifier !== '' && (
									<div
										className={`info-box ${
											!isRejected
												? 'isEligible'
												: 'isNotEligible'
										}`}
									>
										<h3>
											{isRejected
												? 'You are rejected from this drive.'
												: 'This drive is ongoing.'}
										</h3>
										{eligibility === false && (
											<p
												dangerouslySetInnerHTML={{
													__html: rejectedReason												}}
											></p>
										)}
									</div>
								)}
							
							{jd && (
								<div className="jd">
									<h3>Job Description: </h3>
									<div
										className="rich-text"
										dangerouslySetInnerHTML={{
											__html: jd,
										}}
									></div>
								</div>
							)}
							{resources && resources.length !== 0 && (
								<div className="info-box info">
									<h3>Resources for preparation</h3>
									<p
										dangerouslySetInnerHTML={{
											__html: linkifyResources(resources),
										}}
									></p>
								</div>
							)}
						</div>
						{popupSource !== 'applications' &&
							jobDetailsPopup.identifier !== '' && (
								<div className="popup-footer">
									<button
										onClick={() => {
											if (eligibility) {
												handleClose();
												setApplyPopup({
													isOpen: true,
													identifier: id,
												});
											}
										}}
										disabled={!eligibility}
									>
										{eligibility && <>Apply now</>}
										{!eligibility && <>Not Eligible</>}
									</button>
									{deadline && (
										<div className="deadline">
											<img src={time} />
											<span>Apply Before:</span>{' '}
											{formatDate(deadline)}
										</div>
									)}
								</div>
							)}
					</>
				)}
			</div>
		</Dialog>
	);
};

export default JobDetailsPopup;
